import { Circle, FeatureGroup, Marker, Popup, Rectangle, Polygon, TileLayer, useMap } from 'react-leaflet';

import useRasterCoords from './useRasterCoords';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { roomToCoordinate, zoneToRoomToPolygonCoord, zoneToAllScalingFactor, zoneToAllOffset } from './roomCoordinates';
import { PatientVisit } from '../../amplify/api-gateway/search-patients';
import { useMemo } from 'react';

interface LabelLayerProps {
    rc: any
    patients: PatientVisit[];
    selectedZone: string;
    modelPredictions: any[];
    mapHighlightType: string;
    setInternalCSN: (internalCSN?: string) => void;
    setSelectedRoom: (room: string) => void;
}

const LabelLayer = ({
    rc,
    patients,
    selectedZone,
    modelPredictions,
    mapHighlightType,
    setInternalCSN,
    setSelectedRoom,
}: LabelLayerProps) => {

    const roomToPatient = new Map(patients.map(pt => [pt.bed, pt]));
    const csnToModelPrediction = modelPredictions ? new Map(modelPredictions.map(pred => [pred.csn, pred])) : new Map();

    const blueOptions = { color: '#24b4d1', fillColor: '#24b4d1' }
    const redOptions = { fillColor: 'red' }
    const greenOptions = { color: 'green' }
    const greyOptions = { color: 'grey' }
    const purpleOptions = { color: 'purple' }

    // const HEATMAP_COLORS = [
    //     { "color": "#0000FF", "fillColor": "#3366FF" },
    //     { "color": "#3366FF", "fillColor": "#6699FF" },
    //     { "color": "#6699FF", "fillColor": "#99CCFF" },
    //     { "color": "#99CCFF", "fillColor": "#CCE5FF" },
    //     { "color": "#CCDDFF", "fillColor": "#E6F2FF" },
    //     { "color": "#FFCCCC", "fillColor": "#FFE5E5" },
    //     { "color": "#FF9999", "fillColor": "#FFB3B3" },
    //     { "color": "#FF6666", "fillColor": "#FF8080" },
    //     { "color": "#FF3333", "fillColor": "#FF6666" },
    //     { "color": "#FF0000", "fillColor": "#FF3333" }
    // ]

    const HEATMAP_COLORS = [
        { color: '#0000ff', fillColor: '#3333ff' },
        { color: '#3333ff', fillColor: '#6666ff' },
        { color: '#6666ff', fillColor: '#9999ff' },
        { color: '#9999ff', fillColor: '#ccccff' },
        { color: '#ccccff', fillColor: '#ffffcc' },
        { color: '#ffffcc', fillColor: '#ffcc99' },
        { color: '#ffcc99', fillColor: '#ff9966' },
        { color: '#ff9966', fillColor: '#ff6633' },
        { color: '#ff6633', fillColor: '#ff3300' },
        { color: '#ff3300', fillColor: '#ff0000' }
    ];

    const renderPolygon = (coords: number[][], room: string, zone: string) => {
        const projectedCoords: any = coords.map(pos => {
            let x = pos[0];
            let y = pos[1];
            if (selectedZone === "ALL") {
                // @ts-ignore
                x = zoneToAllScalingFactor.get(zone) * x;
                // @ts-ignore
                x = zoneToAllOffset.get(zone)[0] + x;
                // @ts-ignore
                y = zoneToAllScalingFactor.get(zone) * y;
                // @ts-ignore
                y = zoneToAllOffset.get(zone)[1] + y;
            }
            return [
                rc.unproject([x, y]).lat,
                rc.unproject([x, y]).lng,
            ]
        })

        if (mapHighlightType === "LOS") {
            let color = greyOptions;
            if (roomToPatient.has(room)) {
                const pt = roomToPatient.get(room)!;
                let timeDifferenceMilli;
                if (pt.departure_time) {
                    timeDifferenceMilli = Math.abs(new Date(pt.departure_time).getTime() - new Date(pt.arrival_time).getTime());
                } else {
                    timeDifferenceMilli = Math.abs(new Date().getTime() - new Date(pt.arrival_time).getTime());
                }
                const elapsedHours = Math.round(timeDifferenceMilli / (3600 * 1000));
                if (elapsedHours >= HEATMAP_COLORS.length) {
                    color = HEATMAP_COLORS[HEATMAP_COLORS.length - 1];
                } else {
                    color = HEATMAP_COLORS[elapsedHours];
                }
            }

            return (
                <FeatureGroup
                    pathOptions={color}
                >
                    <Polygon
                        positions={projectedCoords}
                        eventHandlers={{
                            click: (e) => {
                                setInternalCSN(roomToPatient.get(room)?.internal_csn!);
                                setSelectedRoom(room!);
                            },
                        }}
                    />
                </FeatureGroup>
            )
        } else if (mapHighlightType === "DISPO_RISK") {
            let color = greyOptions;
            if (roomToPatient.has(room)) {
                const pt = roomToPatient.get(room)!;
                if (csnToModelPrediction.size > 0) {
                    const modelPrediction = csnToModelPrediction.get(pt.csn);
                    if (modelPrediction && modelPrediction.prediction && modelPrediction.prediction.length > 0 && modelPrediction.prediction[0]) {
                        const val = Math.floor(parseFloat(modelPrediction.prediction[0]) * 10);
                        color = HEATMAP_COLORS[val];
                    }
                }
            }

            return (
                <FeatureGroup
                    pathOptions={color}
                >
                    <Polygon
                        positions={projectedCoords}
                        eventHandlers={{
                            click: (e) => {
                                setInternalCSN(roomToPatient.get(room)?.internal_csn!);
                                setSelectedRoom(room!);
                            },
                        }}
                    />
                </FeatureGroup>
            )
        } else if (roomToPatient.has(room)) {
            return (
                <FeatureGroup
                    pathOptions={blueOptions}
                >
                    {/* <Circle
                        center={rc.unproject([coord[0], coord[1]])}
                        radius={4}
                        eventHandlers={{
                            click: (e) => {
                                setInternalCSN(roomToPatient.get(k)?.internal_csn!);
                                setSelectedRoom(k!);
                            },
                        }}
                    /> */}
                    {/* <Rectangle bounds={[
                        // @ts-ignore
                        bounds1, bounds2,
                    ]} /> */}
                    <Polygon
                        positions={projectedCoords}
                        eventHandlers={{
                            click: (e) => {
                                setInternalCSN(roomToPatient.get(room)?.internal_csn!);
                                setSelectedRoom(room!);
                            },
                        }}
                    />
                </FeatureGroup>
            )
        } else {
            return (
                <FeatureGroup pathOptions={greyOptions}>
                    {/* <Circle
                        center={rc.unproject([coord[0], coord[1]])}
                        radius={4}
                        eventHandlers={{
                        click: (e) => {
                            setInternalCSN();
                            setSelectedRoom(k!);
                        },
                    }}
                    /> */}

                    <Polygon
                        positions={projectedCoords}
                        eventHandlers={{
                            click: (e) => {
                                setInternalCSN();
                                setSelectedRoom(room!);
                            },
                        }}
                    />
                </FeatureGroup>
            )
        }
    }

    return (
        <>
            {
                selectedZone === "ALL" && Array.from(zoneToRoomToPolygonCoord.keys()).flatMap((zone: string) => {
                    // @ts-ignore
                    return Array.from(zoneToRoomToPolygonCoord.get(zone)?.keys()).map((k) => {
                        const zoneCoords = zoneToRoomToPolygonCoord.get(zone);
                        if (!zoneCoords) {
                            return <></>
                        }
                        const coords = zoneCoords.has(k) ? zoneCoords.get(k) : null;
                        if (!coords) {
                            return <></>
                        }
                        
                        return renderPolygon(coords, k, zone);
                    })
                })
            }
            {
                // @ts-ignore
                selectedZone !== "ALL" && zoneToRoomToPolygonCoord.get(selectedZone) && Array.from(zoneToRoomToPolygonCoord.get(selectedZone)?.keys()).map((k) => {
                    // const coord = roomToCoordinate.get(k)!;
                    const zoneCoords = zoneToRoomToPolygonCoord.get(selectedZone);
                    if (!zoneCoords) {
                        return <></>
                    }
                    const coords = zoneCoords.has(k) ? zoneCoords.get(k) : null;
                    if (!coords) {
                        return <></>
                    }
                    
                    return renderPolygon(coords, k, selectedZone);
                })
            }
        </>
    );

};

export default LabelLayer;
